import { CommonModule, isPlatformBrowser } from "@angular/common";
import { NavigationStart, RouterLink, RouterLinkActive } from "@angular/router";
import { AlertController, IonicModule, ToastController } from "@ionic/angular";
import { Component, forwardRef, Inject, PLATFORM_ID } from "@angular/core";
import { ActivatedRoute, Router, RouterEvent } from "@angular/router";
import {
	MenuController,
	ModalController,
	NavController,
	Platform,
} from "@ionic/angular";
import { filter } from "rxjs/operators";
import { ErrorService } from "./core/services/error.service";
import { ModalService } from "./core/services/modal.service";
import { NavigationService } from "./core/services/navigation.service";
import { UserService } from "./core/services/user.service";
import { SystemLogsheetService } from "./core/services/systemLogsheet.service";
import { WindowService } from "./core/services/window.service";
import { HomeButtonComponent } from "./shared/components/home-button/home-button.component";
import { MenuService } from "./core/services/menu.service";
import { SwUpdate, VersionReadyEvent } from "@angular/service-worker";
import { Capacitor } from "@capacitor/core";
import { SplashScreen } from "@capacitor/splash-screen";
import { StatusBar, Style } from "@capacitor/status-bar";
import {
	isNativeIos,
	supportsBrowserServiceWorker,
} from "./core/utils/browser-support";
import { MARKETING_NAV_ITEMS } from "./marketing/marketing-nav-items";
import { PublicSeoService } from "./core/services/public-seo.service";
import { ImageLightboxComponent } from "./shared/components/image-lightbox/image-lightbox.component";
interface MenuItems {
	displayName: string;
	page: string;
	icon: string;
}
@Component({
	selector: "app-root",
	templateUrl: "app.component.html",
	styleUrls: ["app.component.scss"],
	standalone: true,
	imports: [
		IonicModule,
		RouterLink,
		RouterLinkActive,
		CommonModule,
		HomeButtonComponent,
		ImageLightboxComponent,
	],
})
export class AppComponent {
	menuItems: Array<MenuItems> = [];
	public readonly isNativeIos = isNativeIos();
	public readonly publicMenuItems = [
		{ displayName: "Home", page: "/" },
		...MARKETING_NAV_ITEMS.map((item) => ({
			displayName: item.navLabel,
			page: item.basePath,
		})),
		{ displayName: "Upcoming Events", page: "/events" },
	];
	public selectedItem: string;
	public dark: boolean;
	public ownersExpanded: boolean = false;
	public ridersExpanded: boolean = false;
	public showAppMenu: boolean = false;
	private readonly isNativePlatform = Capacitor.isNativePlatform();
	private readonly isBrowser: boolean;
	private refreshing = false;
	private updatePromptOpen = false;
	constructor(
		public platform: Platform,
		@Inject(forwardRef(() => UserService)) public userService: UserService,
		public navCtrl: NavController,
		public route: ActivatedRoute,
		public modalCtrl: ModalController,
		@Inject(forwardRef(() => NavigationService))
		public navService: NavigationService,
		@Inject(forwardRef(() => ModalService)) public modalService: ModalService,
		public router: Router,
		public errorService: ErrorService,
		@Inject(forwardRef(() => SystemLogsheetService))
		public systemLogsheetService: SystemLogsheetService,
		public menu: MenuController,
		public windowService: WindowService,
		private swUpdate: SwUpdate,
		private toastCtrl: ToastController,
		private alertCtrl: AlertController,
		public menuService: MenuService,
		private readonly publicSeo: PublicSeoService,
		@Inject(PLATFORM_ID) platformId: object
	) {
		this.isBrowser = isPlatformBrowser(platformId);
		this.router.events
			.pipe(filter((event) => event instanceof NavigationStart))
			.subscribe(() => this.publicSeo.setNoIndex());
		this.initializeApp();
		this.selectedItem = "home";
		this.dark = true;
	}

	initializeApp() {
		let that = this;
		this.menuItems.push({
			displayName: "Rider",
			page: "home",
			icon: "home",
		});

		if (!this.isBrowser) {
			return;
		}

		this.windowService.determineWindowSize();
		this.applyNativeShellClass();

		this.platform.ready().then(async () => {
			await this.initializeNativeShell();
			this.initializePwaShell();
			this.updateAppShellVisibility(this.router.url);

			window.addEventListener("unhandledrejection", function (event) {
				console.warn(
					"WARNING: Unhandled promise rejection. Shame on you! Reason: " +
						event.reason
				);
				that.errorService.handleError(event.reason);
			});

			document.documentElement.addEventListener(
				"gesturestart",
				function (event) {
					console.log("PREVENTING ZOOM");
					event.preventDefault();
				},
				false
			);

			this.router.events
				.pipe(filter((e) => e instanceof RouterEvent))
				.subscribe((e) => {
					let event = e as RouterEvent;
					let end =
						event.url.indexOf(";") == -1
							? event.url.length
							: event.url.indexOf(";");
					let pageStr = event.url.substring(event.url.indexOf("/") + 1, end);
					if (pageStr) {
						this.selectedItem = pageStr;
					}
					this.updateAppShellVisibility(event.url);
				});

			this.windowService.sizeChanged$.subscribe(() => {
				this.updateAppShellVisibility(this.router.url);
			});
		});

	}

	toggleSubMenu(): void {
		this.ownersExpanded = !this.ownersExpanded;
	}

	toggleRiderSubMenu(): void {
		this.ridersExpanded = !this.ridersExpanded;
	}

	private updateAppShellVisibility(url: string): void {
		this.showAppMenu = true;
	}

	private applyNativeShellClass() {
		if (!this.isNativePlatform) {
			return;
		}

		document.documentElement.classList.add("capacitor-native");
		document.body?.classList.add("capacitor-native");
	}

	private async initializeNativeShell() {
		if (!this.isNativePlatform) {
			return;
		}

		await this.configureStatusBar();

		try {
			await SplashScreen.hide();
			console.log("Native splash hidden");
		} catch (error) {
			console.warn("SplashScreen.hide failed", error);
		}
	}

	private initializePwaShell() {
		if (!supportsBrowserServiceWorker()) {
			console.log("Browser service worker disabled for this platform.");
			return;
		}

		this.initializeInstallPrompt();
		this.initializeServiceWorkerUpdatePrompt();
		this.initializeServiceWorkerControllerReload();
	}

	private initializeInstallPrompt() {
		let deferredPrompt: any;

		window.addEventListener("beforeinstallprompt", async (e: any) => {
			e.preventDefault();
			deferredPrompt = e;
			const toast = await this.toastCtrl.create({
				message: "Install App?",
				position: "bottom",
				buttons: [
					{
						text: "Install",
						role: "info",
						handler: () => {
							if (deferredPrompt) {
								deferredPrompt.prompt();
								deferredPrompt.userChoice.then((choiceResult) => {
									if (choiceResult.outcome === "accepted") {
										console.log("User accepted the install prompt");
									} else {
										console.log("User dismissed the install prompt");
									}
									deferredPrompt = null;
								});
							}
						},
					},
					{
						text: "Dismiss",
						role: "cancel",
						handler: () => {
							console.log("Dismiss clicked");
						},
					},
				],
				duration: 10000,
			});

			await toast.present();
			console.log("'beforeinstallprompt' event was fired.");
		});
	}

	private initializeServiceWorkerUpdatePrompt() {
		if (!this.swUpdate.isEnabled) {
			return;
		}

		this.swUpdate.versionUpdates
			.pipe(filter((event): event is VersionReadyEvent => event.type === "VERSION_READY"))
			.subscribe(async () => {
				if (this.refreshing || this.updatePromptOpen) return;
				this.updatePromptOpen = true;

				try {
					const alert = await this.alertCtrl.create({
						header: "Update available",
						message: "A new version of FlaggerAI is ready. Update now to reload the app, or choose Later to keep working.",
						backdropDismiss: false,
						buttons: [
							{
								text: "Later",
								role: "cancel",
							},
							{
								text: "Update now",
								handler: async () => {
									await this.activateAndReload();
								},
							},
						],
					});

					await alert.present();
					await alert.onDidDismiss();
				} catch (error) {
					console.warn("Unable to present app update alert", error);
				} finally {
					this.updatePromptOpen = false;
				}
			});
	}

	private initializeServiceWorkerControllerReload() {
		navigator.serviceWorker.addEventListener("controllerchange", () => {
			if (this.refreshing) return;
			this.refreshing = true;
			window.location.reload();
		});
	}

	private async activateAndReload() {
		if (this.refreshing) return;
		this.refreshing = true;

		try {
			await this.swUpdate.activateUpdate();
		} catch (error) {
			console.warn("activateUpdate failed; reloading anyway", error);
		}

		window.location.reload();
	}

	private async configureStatusBar() {
		await this.tryStatusBarCall("StatusBar.setStyle", () =>
			StatusBar.setStyle({ style: Style.Dark })
		);
	}

	private async tryStatusBarCall(
		label: string,
		action: () => Promise<void>
	): Promise<void> {
		try {
			await action();
			console.log(`${label} succeeded.`);
		} catch (error) {
			console.warn(`${label} failed. CSS safe-area fallback remains active.`, error);
		}
	}
}
